<script setup>
import { ref, computed, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  assistantId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['close']);

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const searchQuery = ref('');
const processingIds = ref(new Set());
const batchSelectedIds = ref(new Set());
const isBatchSubmitting = ref(false);
const hoveredToolId = ref(null);

// 所有工具列表（账户级别）
const allTools = useMapGetter('pilotCustomTools/getRecords');
const uiFlags = useMapGetter('pilotCustomTools/getUIFlags');
const isLoading = computed(() => uiFlags.value.fetchingList);

// 从 store 获取已关联的工具列表
const assistantTools = useMapGetter('pilotAssistantCustomTools/getRecords');

// 已关联的工具 ID 集合
const linkedIdsSet = computed(() => {
  const ids = (assistantTools.value || []).map(tool => tool.id);
  return new Set(ids);
});

// 根据搜索显示的工具列表
const displayedTools = computed(() => {
  let list = allTools.value || [];

  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    list = list.filter(tool => {
      const title = (tool.title || '').toLowerCase();
      const description = (tool.description || '').toLowerCase();
      return title.includes(query) || description.includes(query);
    });
  }

  return list;
});

// 加载所有工具
const fetchAllTools = async () => {
  try {
    await store.dispatch('pilotCustomTools/get', {});
  } catch (error) {
    // 忽略错误
  }
};

// 刷新已关联的工具列表
const refreshLinkedTools = async () => {
  try {
    await store.dispatch('pilotAssistantCustomTools/get', {
      assistantId: props.assistantId,
    });
  } catch (error) {
    // 忽略错误
  }
};

// 判断工具是否已关联
const isLinked = toolId => linkedIdsSet.value.has(toolId);

// 判断工具是否正在处理中
const isProcessing = toolId => processingIds.value.has(toolId);

// 批量选择相关
const isSelected = toolId => batchSelectedIds.value.has(toolId);

const toggleSelection = toolId => {
  if (batchSelectedIds.value.has(toolId)) {
    batchSelectedIds.value.delete(toolId);
  } else {
    batchSelectedIds.value.add(toolId);
  }
  batchSelectedIds.value = new Set(batchSelectedIds.value);
};

const hasSelection = computed(() => batchSelectedIds.value.size > 0);

const buildSelectedCountLabel = computed(() => {
  const count = displayedTools.value.length;
  const isAllSelected = batchSelectedIds.value.size === count && count > 0;
  return isAllSelected ? `取消全选 (${count})` : `全选 (${count})`;
});

const selectedCountLabel = computed(() => {
  return `已选择 ${batchSelectedIds.value.size} 项`;
});

const toggleSelectAll = () => {
  const allItems = displayedTools.value;
  const allSelected =
    batchSelectedIds.value.size === allItems.length && allItems.length > 0;

  if (allSelected) {
    batchSelectedIds.value = new Set();
  } else {
    batchSelectedIds.value = new Set(allItems.map(tool => tool.id));
  }
};

// 获取认证类型标签
const getAuthTypeLabel = authType => {
  const typeMap = {
    none: t('PILOT.CUSTOM_TOOLS.FORM.AUTH_TYPES.NONE'),
    bearer: t('PILOT.CUSTOM_TOOLS.FORM.AUTH_TYPES.BEARER'),
    basic: t('PILOT.CUSTOM_TOOLS.FORM.AUTH_TYPES.BASIC'),
    api_key: t('PILOT.CUSTOM_TOOLS.FORM.AUTH_TYPES.API_KEY'),
  };
  return typeMap[authType] || authType || '';
};

// 切换工具关联状态
const toggleToolLink = async (tool, action) => {
  processingIds.value.add(tool.id);
  processingIds.value = new Set(processingIds.value);

  try {
    if (action === 'add') {
      await store.dispatch('pilotAssistantCustomTools/create', {
        assistantId: props.assistantId,
        customToolId: tool.id,
      });
      useAlert(t('PILOT.ASSISTANTS.CONFIG.TOOLS.ADD.SUCCESS'));
    } else {
      await store.dispatch('pilotAssistantCustomTools/delete', {
        assistantId: props.assistantId,
        id: tool.id,
      });
      useAlert(t('PILOT.ASSISTANTS.CONFIG.TOOLS.REMOVE.SUCCESS'));
    }
    await refreshLinkedTools();
  } catch (error) {
    useAlert(
      error?.message ||
        t('PILOT.ASSISTANTS.CONFIG.TOOLS.SELECTOR.OPERATION_ERROR')
    );
  } finally {
    processingIds.value.delete(tool.id);
    processingIds.value = new Set(processingIds.value);
  }
};

// 批量操作
const handleBatchOperation = async action => {
  if (!hasSelection.value || isBatchSubmitting.value) return;

  isBatchSubmitting.value = true;
  try {
    let ids = Array.from(batchSelectedIds.value);

    // 添加时过滤掉已关联的工具
    if (action === 'add') {
      ids = ids.filter(id => !linkedIdsSet.value.has(id));
    }
    // 移除时过滤掉未关联的工具
    if (action === 'remove') {
      ids = ids.filter(id => linkedIdsSet.value.has(id));
    }

    if (ids.length === 0) {
      batchSelectedIds.value = new Set();
      return;
    }

    const dispatchAction =
      action === 'add'
        ? 'pilotAssistantCustomTools/create'
        : 'pilotAssistantCustomTools/delete';

    await Promise.all(
      ids.map(toolId => {
        const args = { assistantId: props.assistantId };
        if (action === 'add') {
          args.customToolId = toolId;
        } else {
          args.id = toolId;
        }
        return store.dispatch(dispatchAction, args);
      })
    );
    await refreshLinkedTools();
    batchSelectedIds.value = new Set();
    if (action === 'add') {
      useAlert(t('PILOT.ASSISTANTS.CONFIG.TOOLS.ADD.SUCCESS'));
    } else {
      useAlert(t('PILOT.ASSISTANTS.CONFIG.TOOLS.REMOVE.SUCCESS'));
    }
  } catch (error) {
    useAlert(
      error?.message ||
        t('PILOT.ASSISTANTS.CONFIG.TOOLS.SELECTOR.OPERATION_ERROR')
    );
  } finally {
    isBatchSubmitting.value = false;
  }
};

// 关闭弹窗
const handleClose = () => {
  emit('close');
};

const closeDialog = () => {
  dialogRef.value?.close();
};

// 打开弹窗
const open = async () => {
  searchQuery.value = '';
  batchSelectedIds.value = new Set();
  dialogRef.value?.open();
  await Promise.all([refreshLinkedTools(), fetchAllTools()]);
};

defineExpose({
  dialogRef,
  open,
  close: handleClose,
});
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="t('PILOT.ASSISTANTS.CONFIG.TOOLS.SELECTOR.TITLE')"
    :show-cancel-button="false"
    :show-confirm-button="false"
    width="xl"
    overflow-y-auto
    @close="handleClose"
  >
    <div class="flex flex-col gap-4">
      <!-- 搜索工具条 - 无选中时显示 -->
      <div v-if="!hasSelection" class="flex items-center gap-2">
        <Input
          v-model="searchQuery"
          type="search"
          size="sm"
          :placeholder="
            t('PILOT.ASSISTANTS.CONFIG.TOOLS.SELECTOR.SEARCH_PLACEHOLDER')
          "
          :custom-input-class="[
            'h-8 [&:not(.focus)]:!border-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-8 !py-1 rtl:!pr-8',
          ]"
          class="flex-1"
        >
          <template #prefix>
            <Icon
              icon="i-lucide-search"
              class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2 rtl:right-2"
            />
          </template>
        </Input>
      </div>

      <!-- 批量操作工具条 - 有选中时显示 -->
      <div v-else class="flex items-center gap-3 px-3">
        <div class="flex items-center gap-2">
          <Checkbox
            :model-value="
              batchSelectedIds.size === displayedTools.length &&
              displayedTools.length > 0
            "
            :indeterminate="
              batchSelectedIds.size > 0 &&
              batchSelectedIds.size < displayedTools.length
            "
            @update:model-value="toggleSelectAll"
          />
          <span class="text-sm font-medium text-n-slate-12 tabular-nums">
            {{ buildSelectedCountLabel }}
          </span>
        </div>
        <span class="text-sm text-n-slate-10 truncate tabular-nums">
          {{ selectedCountLabel }}
        </span>
        <div class="h-4 w-px bg-n-strong" />
        <Button
          :label="t('PILOT.ASSISTANTS.CONFIG.TOOLS.SELECTOR.BATCH_ADD')"
          size="sm"
          variant="ghost"
          color="slate"
          :is-loading="isBatchSubmitting"
          @click="handleBatchOperation('add')"
        />
        <Button
          :label="t('PILOT.ASSISTANTS.CONFIG.TOOLS.SELECTOR.BATCH_REMOVE')"
          size="sm"
          variant="ghost"
          color="ruby"
          :is-loading="isBatchSubmitting"
          @click="handleBatchOperation('remove')"
        />
      </div>

      <!-- 工具列表 -->
      <div class="flex flex-col gap-2 max-h-96 overflow-y-auto">
        <!-- 加载中 -->
        <div v-if="isLoading" class="flex justify-center py-8">
          <Spinner :size="20" />
        </div>

        <!-- 空状态 -->
        <div
          v-else-if="displayedTools.length === 0"
          class="text-sm text-n-slate-9 text-center py-8"
        >
          {{
            searchQuery
              ? t('PILOT.ASSISTANTS.CONFIG.TOOLS.SELECTOR.EMPTY_SEARCH')
              : t('PILOT.ASSISTANTS.CONFIG.TOOLS.SELECTOR.EMPTY_LIST')
          }}
        </div>

        <!-- 工具列表 -->
        <template v-else>
          <div
            v-for="tool in displayedTools"
            :key="tool.id"
            class="flex items-center gap-3 p-3 rounded-lg border transition-colors"
            :class="[
              isSelected(tool.id)
                ? 'border-n-brand bg-n-brand-50 dark:bg-n-brand-500/10'
                : 'border-n-slate-6 hover:border-n-slate-8 hover:bg-n-slate-1 dark:hover:bg-n-slate-800',
            ]"
            @mouseenter="hoveredToolId = tool.id"
            @mouseleave="hoveredToolId = null"
          >
            <!-- Checkbox - hover或有选中时才显示 -->
            <div
              :class="{
                invisible:
                  hoveredToolId !== tool.id && batchSelectedIds.size === 0,
              }"
            >
              <Checkbox
                :model-value="isSelected(tool.id)"
                @update:model-value="toggleSelection(tool.id)"
                @click.stop
              />
            </div>

            <!-- 工具信息 -->
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2">
                <span class="text-sm text-n-slate-12 truncate">
                  {{ tool.title }}
                </span>
                <!-- 已关联标记 -->
                <span
                  v-if="isLinked(tool.id)"
                  class="px-1.5 py-0.5 text-xs font-medium text-white whitespace-nowrap rounded bg-n-ruby-9"
                >
                  已关联
                </span>
              </div>
              <div class="text-xs text-n-slate-10 mt-0.5 truncate">
                {{
                  tool.description ||
                  t('PILOT.ASSISTANTS.CONFIG.TOOLS.SELECTOR.NO_DESCRIPTION')
                }}
              </div>
              <!-- 认证方式 -->
              <span
                v-if="tool.auth_type && tool.auth_type !== 'none'"
                class="text-xs text-n-slate-10 mt-1 inline-flex items-center gap-1"
              >
                <i class="i-lucide-lock text-sm" />
                {{ getAuthTypeLabel(tool.auth_type) }}
              </span>
            </div>

            <!-- 添加/移除按钮 -->
            <Button
              v-if="isLinked(tool.id)"
              icon="i-lucide-minus"
              size="sm"
              variant="faded"
              color="ruby"
              :is-loading="isProcessing(tool.id)"
              :disabled="isProcessing(tool.id)"
              @click="toggleToolLink(tool, 'remove')"
            />
            <Button
              v-else
              icon="i-lucide-plus"
              size="sm"
              variant="faded"
              color="slate"
              :is-loading="isProcessing(tool.id)"
              :disabled="isProcessing(tool.id)"
              @click="toggleToolLink(tool, 'add')"
            />
          </div>
        </template>
      </div>

      <!-- 底部按钮 -->
      <div
        class="flex items-center justify-end gap-2 pt-4 border-t border-n-slate-6"
      >
        <Button
          :label="t('PILOT.ASSISTANTS.CONFIG.TOOLS.SELECTOR.CLOSE')"
          variant="faded"
          color="slate"
          @click="closeDialog"
        />
      </div>
    </div>
  </Dialog>
</template>
